<template>
  <div
    :data-testid="$attrs['data-testid'] ? `${$attrs['data-testid']}-wrapper` : null"
    class="ata-Field"
    :class="{ 'ata-Field-copyable': copyable }"
  >
    <textarea
      ref="textarea"
      v-model="localValue"
      :class="{ 'ata-Field_Input-error': isError, 'ata-Field_Input-copyable': copyable }"
      :maxlength="maxLength"
      :placeholder="placeholder"
      :readonly="readonly"
      :rows="rows"
      class="ata-Field_Input"
      v-bind="bindingAttrs"
      @blur="$emit('blur')"
      @keydown.esc="$emit('blur')"
    />
    <div class="ata-Field_Actions">
      <AppButton
        v-if="copyable && !isStringEmpty(modelValue)"
        height="24"
        icon="copy-next"
        remove-padding
        type="default"
        width="24"
        @click="onCopyText"
      />
    </div>
    <AppFieldError v-if="isError" :show="isError" class="ata-Field_Error">
      <slot name="error-text">
        {{ $t('field.required') }}
      </slot>
    </AppFieldError>
  </div>
</template>

<script setup>
import { useTextareaAutosize } from '@vueuse/core'
import { computed, watch, nextTick, useAttrs } from 'vue'
import { useI18n } from 'vue-i18n'

import { useTabindexAttrs } from '@/composables/tabindex'
import { copyToClipboard, isStringEmpty } from '@/utils/general'
import { showNotify } from '@/utils/notify'

import AppFieldError from '@/components/form/AppFieldError'
import AppButton from '@/components/ui/AppButton/AppButton'

defineOptions({
  name: 'AppTextarea',
  inheritAttrs: false
})

const { triggerResize, textarea } = useTextareaAutosize({ styleProp: 'minHeight' })
const { t } = useI18n()

const props = defineProps({
  modelValue: {
    required: true,
    type: String
  },

  readonly: {
    type: Boolean
  },

  placeholder: {
    type: String,
    default: ''
  },

  isError: {
    type: Boolean
  },

  maxLength: {
    type: Number,
    default: 512
  },

  rows: {
    type: Number,
    default: 4
  },

  copyable: {
    type: Boolean,
    default: false
  }
})

watch(
  () => props.modelValue,
  () => {
    nextTick(() => {
      triggerResize()
    })
  },
  { immediate: true }
)

const emit = defineEmits(['blur', 'update:model-value'])

const focus = () => {
  textarea.value.focus()
}

const onCopyText = () => {
  copyToClipboard(props.modelValue)
  showNotify({ title: t('text_copied') })
}

defineExpose({ focus })

// const resizeObserver = ref(null)

// const textareaWidth = ref(null)

// watch(textareaWidth, (newValue, oldValue) => {
//   if (newValue !== oldValue && oldValue !== null) {
//     nextTick(() => {
//       triggerResize()
//     })
//   }
// })

// const setTextareaWidth = () => {
//   if (textarea.value) {
//     textareaWidth.value = textarea.value.offsetWidth
//   }
// }

// const initResizeObserver = () => {
//   resizeObserver.value = new ResizeObserver(() => {
//     setTextareaWidth()
//   })
//
//   resizeObserver.value.observe(textarea.value)
// }

// const disconnectResizeObserver = () => {
//   if (resizeObserver.value) {
//     resizeObserver.value.disconnect()
//     resizeObserver.value = null
//   }
// }

// onMounted(() => {
//   initResizeObserver()
//   setTextareaWidth()
// })

// onBeforeUnmount(() => {
//   disconnectResizeObserver()
// })

const localValue = computed({
  get() {
    return props.modelValue
  },
  set(v) {
    if (!props.readonly) {
      emit('update:model-value', v)
    }
  }
})
const attrs = useAttrs()
const tabindexAttrs = useTabindexAttrs(() => props.readonly)
const bindingAttrs = computed(() => {
  const DATA_TEST_ID = 'data-testid'
  const DATA_AUTO_TEST_ID = 'data-auto-testid'
  const INPUT_ELEMENT = 'textarea-element'
  return {
    ...attrs,
    [DATA_TEST_ID]: attrs[DATA_TEST_ID] ? `${attrs[DATA_TEST_ID]}-${INPUT_ELEMENT}` : null,

    [DATA_AUTO_TEST_ID]: attrs[DATA_AUTO_TEST_ID]
      ? `${attrs[DATA_AUTO_TEST_ID]}-${INPUT_ELEMENT}`
      : null,
    ...tabindexAttrs.value
  }
})
</script>

<style lang="scss" scoped>
.ata-Field {
  display: grid;
  gap: 4px;
  position: relative;
  min-height: 40px;

  &-copyable {
    position: relative;
  }
}

.ata-Field_Actions {
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 1;
}

.ata-Field_Input {
  resize: none;
  overflow: hidden;
  width: 100%;
  background-color: $white;
  border: 2px solid $grey-2-next;
  border-radius: $border-radius-sm-next;
  padding: var(--input-padding, 8px 8px 15px);
  font-family: var(--ata-input-font-family, #{$system-ui});
  min-height: 100px;
  line-height: 19px;
  caret-color: $primary-color-next;
  color: var(--ata-input-color, #{$dark-1});

  &:not(&:read-only) {
    &:focus {
      border-color: $primary-color-next;
    }
  }

  &::placeholder {
    color: $placeholder-color;
  }

  &:-moz-placeholder,
  &::-moz-placeholder {
    color: $grey-1-next;
  }

  &:read-only {
    cursor: not-allowed;
    background-color: var(--ata-readonly-background-color, #{$grey-2-next});
    border-color: var(--ata-readonly-border-color, #{$grey-2-next});
    color: $dark-3;
  }

  &-error {
    border-color: $grade-low-color-next;

    &::placeholder {
      color: inherit;
    }

    &:-moz-placeholder,
    &::-moz-placeholder {
      color: inherit;
    }
  }

  &-copyable {
    padding-right: 40px;
  }
}

.ata-Field_Error {
  justify-self: flex-start;
}
</style>
